import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import type { TakiSdk } from '@taki/sdk';
import { fusionnerBranding, repliDepuisConfig } from '@taki/ui';
import type { BrandingPatch, BrandingPersonnalise, BrandingPort } from '@taki/ui';

/**
 * LE PORT DE BRANDING DE L'HOTE : le nom, le logo et la couleur d'accent de
 * l'environnement, du document Firestore jusqu'au socle.
 *
 * MONTE ICI ET PAS DANS LE SHELL, pour la meme raison que les modeles, les
 * liens et l'acces : c'est l'HOTE qui a le SDK (le shell ne l'a pas dans ses
 * dependances, et c'est voulu). Le socle ne connait que la FORME
 * (`BrandingPort`, @taki/ui) et la REGLE de fusion (`fusionnerBranding`).
 *
 * TROIS DECISIONS VIVENT ICI, ET ELLES SE TIENNENT :
 *
 *  1. L'OBSERVATION COMMENCE AU MONTAGE, AVANT LA CONNEXION. La lecture du
 *     document est PUBLIQUE dans les rules, precisement pour que l'ecran de
 *     connexion porte le nom et le logo du client. Un abonnement qui attendrait
 *     l'auth ferait entrer le client par une porte Taki.
 *  2. LE PREMIER ETAT VIENT DU CACHE LOCAL, LU EN SYNCHRONE. Sans lui, le
 *     premier rendu serait lime, puis basculerait a la couleur du client une
 *     fois le snapshot arrive : un flash de la mauvaise marque a chaque
 *     chargement. Le cache est une COPIE, jamais la verite : le snapshot
 *     l'ecrase des qu'il arrive, et l'efface quand le document disparait.
 *  3. UNE PANNE D'OBSERVATION NE CASSE RIEN. On garde le repli de la config,
 *     on previent en console, et l'application tourne : le branding est du
 *     confort, pas une condition d'usage.
 */

/**
 * La clef du cache local, par ORG : un poste partage entre deux environnements
 * (le cas du support) ne doit pas repeindre l'un avec les couleurs de l'autre.
 */
export function clefDeCache(orgId: string): string {
  return `taki.branding.${orgId}`;
}

/**
 * Le cache lu, ou `null`. TOUT echec rend `null` : un JSON corrompu, un
 * `localStorage` refuse (navigation privee, iframe cloisonnee) ou une valeur
 * qui n'est pas un objet. Le repli de la config prend alors le relais, ce qui
 * est exactement l'etat d'avant ce chantier.
 */
export function lireCache(orgId: string): BrandingPersonnalise | null {
  try {
    const brut = window.localStorage.getItem(clefDeCache(orgId));
    if (brut === null) return null;
    const lu: unknown = JSON.parse(brut);
    if (typeof lu !== 'object' || lu === null || Array.isArray(lu)) return null;
    return lu as BrandingPersonnalise;
  } catch {
    return null;
  }
}

/** Le cache ecrit, ou EFFACE quand le document n'existe plus. Jamais fatal. */
export function ecrireCache(orgId: string, personnalise: BrandingPersonnalise | null): void {
  try {
    if (personnalise === null) window.localStorage.removeItem(clefDeCache(orgId));
    else window.localStorage.setItem(clefDeCache(orgId), JSON.stringify(personnalise));
  } catch {
    /* Un cache qu'on ne peut pas ecrire coute un flash au prochain chargement,
       rien de plus : il n'y a rien a dire a l'utilisateur. */
  }
}

/**
 * Deux personnalisations disent-elles la meme chose ? Sert a NE PAS reposer un
 * apercu identique : `previsualiser` est appele a chaque frappe, et poser un
 * objet neuf a chaque fois recreerait le port, donc l'effet qui l'appelle.
 */
function memeApercu(a: BrandingPatch | null, b: BrandingPatch | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    (a.name ?? null) === (b.name ?? null) &&
    (a.logo ?? null) === (b.logo ?? null) &&
    (a.accent ?? null) === (b.accent ?? null)
  );
}

/**
 * Le port pret a etre passe a `BrandingProvider`.
 *
 * @param peutModifier Le MEME droit que la gestion des membres (owner ou admin
 * actif) : l'hote le tient deja pour le port d'acces, on ne le recalcule pas.
 */
export function useBrandingDeLHote(
  config: TakiConfig,
  sdk: TakiSdk,
  peutModifier: boolean,
): BrandingPort {
  const repli = useMemo(() => repliDepuisConfig(config.branding), [config.branding]);

  /* L'ETAT INITIAL EST LU EN SYNCHRONE, dans l'initialiseur paresseux du
     `useState` : une lecture dans un effet arriverait apres le premier rendu,
     donc apres le flash qu'on cherche a eviter. */
  const [personnalise, setPersonnalise] = useState<BrandingPersonnalise>(
    () => lireCache(config.orgId) ?? {},
  );
  const [pret, setPret] = useState(false);
  const [apercu, setApercu] = useState<BrandingPatch | null>(null);

  useEffect(() => {
    let vivant = true;
    let arreter: (() => void) | undefined;
    try {
      arreter = sdk.branding.observer(
        (valeur) => {
          if (!vivant) return;
          setPersonnalise(valeur ?? {});
          ecrireCache(config.orgId, valeur);
          setPret(true);
        },
        (cause: unknown) => {
          if (!vivant) return;
          console.warn("Branding de l'environnement : lecture impossible.", cause);
          setPret(true);
        },
      );
    } catch (cause) {
      console.warn("Branding de l'environnement : observation impossible.", cause);
      setPret(true);
    }
    return () => {
      vivant = false;
      arreter?.();
    };
  }, [sdk, config.orgId]);

  /* L'APERCU NE SE REPOSE PAS S'IL NE CHANGE RIEN : sans cette garde, l'ecran
     Environnement pousserait un objet neuf a chaque frappe, le port changerait
     d'identite, et tout effet qui en depend tournerait en boucle. */
  const previsualiser = useCallback((suivant: BrandingPatch | null): void => {
    setApercu((actuel) => (memeApercu(actuel, suivant) ? actuel : suivant));
  }, []);

  return useMemo<BrandingPort>(
    () => ({
      /* L'APERCU REMPLACE TOUTE LA PERSONNALISATION, il ne s'y ajoute pas : le
         brouillon de l'ecran porte DEJA les trois champs, et un merge ferait
         survivre un logo qu'on vient de retirer. */
      courant: fusionnerBranding(repli, apercu ?? personnalise),
      personnalise,
      repli,
      pret,
      peutModifier,
      enregistrer: async (patch) => {
        await sdk.branding.enregistrer(patch);
        /* L'APERCU TOMBE A L'ENREGISTREMENT : ce qui s'affiche redevient le
           document, que le snapshot vient de mettre a jour. Le garder ferait
           du brouillon la source d'affichage pour toujours. */
        setApercu(null);
      },
      reinitialiser: async () => {
        await sdk.branding.reinitialiser();
        setApercu(null);
      },
      previsualiser,
    }),
    [repli, personnalise, apercu, pret, peutModifier, sdk, previsualiser],
  );
}
